/* eslint-disable */
import { LoadingOutlined, ZoomOutOutlined } from '@ant-design/icons';
import {
  Button,
  Card,
  Col,
  DatePicker,
  Form,
  Input,
  Row,
  Spin,
  Table,
  Tabs,
  Tag,
  message
} from 'antd';
import type { ColumnsType, SorterResult } from 'antd/es/table/interface';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import InstitutionUserService from '../../services/InstitutionUserService';
import { parseMoneyAmount } from '../../utils/parseMoneyAmount';
import DynamicTable from '../common/DynamicTable';
import { RefundStatsRangeAlert } from './RefundStatsRangeAlert';

const antIcon = <LoadingOutlined style={{ fontSize: 'var(--base-font-size)', color: '#37762F' }} spin />;
const { RangePicker } = DatePicker;

interface DataType {
  index: any;
  key: React.Key;
  bank_code: string;
  transaction_reference: string;
  debit_account_number: string;
  debit_account_name: string;
  beneficiary_account_name: string;
  beneficiary_bank_name: string;
  beneficiary_account_number: string;
  transaction_currency: string;
  amount: number;
  reason: string;
  status: string;
  created_at: string;
  updated_at: string;
  totalPayments: string;
  amountRequestedToBeRefund: string;
  amountRequestedToBeRefundedInProgressStatus: string;
  TotalNumberOfApplicantRequestedRefund: string;
  notRequestedBidders: string;
}

const RefundRequestList: React.FC = () => {
  const [form] = Form.useForm();
  const userInfo = JSON.parse(localStorage.getItem('userInfo') ?? '{}');
  const navigate = useNavigate();

  const [filterStatus, setfilterStatus] = useState('');
  const [clear, setClear] = useState(false);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [refundRequestList, setRefundRequestList] = useState<DataType[]>([]);
  const [bailiffRefundRequestList, setBailiffRefundRequestList] = useState<DataType[]>([]);
  const [loading, setLoading] = useState(false);
  const [progressVisible, setProgressVisible] = useState(false);
  const [progressPercent, setProgressPercent] = useState(0);
  const [sortedInfo, setSortedInfo] = useState<SorterResult<DataType>>({});
  const [pagination, setPagination] = useState<{ current: number; pageSize: number; total?: number }>({
    current: 1,
    pageSize: 10,
    total: 0
  });

  const [stats, setStats] = useState<DataType | null>(null);
  const [totalAmount, setTotalAmount] = useState(0);
  const [activeTab, setActiveTab] = useState<'bidders' | 'bailiffs'>('bidders'); // Default tab
  const [token, setToken] = useState('');
  const [BiddersAmountRequestedToBeRefund, setBiddersAmountRequestedToBeRefund] = useState('');
  const [BailiffAmountRequestedToBeRefunded, setBailiffAmountRequestedToBeRefunded] = useState('');
  const [NewBiddersAmountRequestedToBeRefunded, setNewBiddersAmountRequestedToBeRefunded] =
    useState('');
  const [NewBailiffAmountRequestedToBeRefunded, setNewBailiffAmountRequestedToBeRefunded] =
    useState('');
  const [totalPaidForRange, setTotalPaidForRange] = useState('');

  // Simulated page loader
  useEffect(() => {
    handlePageReload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch stats by date range
  useEffect(() => {
    async function fetchRefundStats() {
      setLoading(true);
      try {
        const response = await InstitutionUserService.viewRefundStatsByRefundStartDate(
          fromDate,
          toDate,
          token
        );

        setBiddersAmountRequestedToBeRefund(response.data?.biddersAmountRequestedToBeRefund ?? '');
        setBailiffAmountRequestedToBeRefunded(response.data?.bailiffAmountRequestedToBeRefunded ?? '');
        setNewBiddersAmountRequestedToBeRefunded(
          response.data?.newBiddersAmountRequestedToBeRefunded ?? ''
        );
        setNewBailiffAmountRequestedToBeRefunded(
          response.data?.newBailiffAmountRequestedToBeRefunded ?? ''
        );
        const tp = response.data?.totalPayments;
        setTotalPaidForRange(tp != null && tp !== '' ? String(tp) : '');
      } catch (err: any) {
        // Replaced undefined setError with message.error
        message.error('Failed to fetch refund stats');
        // eslint-disable-next-line no-console
        console.error(err);
      } finally {
        setLoading(false);
      }
    }

    void fetchRefundStats();
  }, [fromDate, toDate, token]);

  // initial load of data
  useEffect(() => {
    OnRefundRequestSearch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handlePageReload = () => {
    setProgressVisible(true);
    setLoading(true);
    setTimeout(() => {
      setLoading(false);
      setProgressVisible(false);
    }, 5000);
  };

  useEffect(() => {
    if (activeTab === 'bidders') {
      const total = refundRequestList.reduce((acc, curr) => acc + Number(curr.amount || 0), 0);
      setTotalAmount(total);
    } else {
      const total = bailiffRefundRequestList.reduce((acc, curr) => acc + Number(curr.amount || 0), 0);
      setTotalAmount(total);
    }
  }, [refundRequestList, bailiffRefundRequestList, activeTab]);

  useEffect(() => {
    InstitutionUserService.RefundRequestStats()
      .then((res: any) => {
        setStats(res.data);
      })
      .catch((err) => {
        message.error(err?.message || 'Failed to fetch stats');
      });
  }, []);

  // Build filter summary string
  let filter = '';
  if (filterStatus) filter = filter + 'Type';
  if (fromDate) filter = filter === '' ? filter + 'From Date' : filter + ', From Date';
  if (toDate) filter = filter === '' ? filter + 'To Date' : filter + ', To Date';

  const columnsToDownload = [
    'bank_code',
    'transaction_reference',
    'debit_account_number',
    'debit_account_name',
    'beneficiary_account_name',
    'beneficiary_bank_name',
    'beneficiary_account_number',
    'amount',
    'transaction_currency',
    'reason'
  ];

  const clearFilter = (): void => {
    setfilterStatus('');
    form.setFieldValue('filterStatus', '');
    form.setFieldValue('range_picker', ['', '']);
    setFromDate('');
    setToDate('');
    setClear(true);
    // refetch after clear
    const resetPagination = { current: 1, pageSize: pagination.pageSize };
    setPagination((prev) => ({ ...prev, current: 1 }));
    OnRefundRequestSearch(undefined, activeTab, resetPagination);
  };

  // When user switches tabs: reset pagination to page 1 and fetch
  const handleTabChange = (key: string) => {
    const tabKey = key === 'bailiffs' ? 'bailiffs' : 'bidders';
    setActiveTab(tabKey as 'bailiffs' | 'bidders');

    const resetPagination = { current: 1, pageSize: pagination.pageSize };
    setPagination((prev) => ({ ...prev, current: 1 }));
    OnRefundRequestSearch(undefined, tabKey, resetPagination);
  };

  // Unified fetch function: event can be an input event OR a string; pageConfig overrides pagination
  const OnRefundRequestSearch = (
    event?: any,
    tabKey?: string,
    pageConfig?: { current: number; pageSize: number }
  ): void => {
    setLoading(true);

    const searchText =
      event && typeof event === 'object' && event?.target
        ? event.target.value
        : typeof event === 'string'
          ? event
          : undefined;

    const currentTab = (tabKey as 'bailiffs' | 'bidders') || activeTab;
    const currentPagination = pageConfig
      ? { current: pageConfig.current, pageSize: pageConfig.pageSize }
      : pagination;

    const service =
      currentTab === 'bidders'
        ? InstitutionUserService.GetRefundRequestList
        : InstitutionUserService.GetNewBailiffRefundRequestList;

    service(
      currentPagination.pageSize as number,
      currentPagination.current as number,
      searchText ?? '',
      fromDate,
      toDate
    )
      .then((res: any) => {
        setLoading(false);

        if (res.status === 200) {
          const data = res?.data ?? [];
          if (!data || data.length === 0) {
            if (currentTab === 'bidders') {
              setRefundRequestList([]);
            } else {
              setBailiffRefundRequestList([]);
            }
            message.info('No New Refund Request found');
          } else {
            if (currentTab === 'bidders') {
              setRefundRequestList(data);
            } else {
              setBailiffRefundRequestList(data);
            }
            setPagination((prev) => ({
              ...prev,
              total: res?.meta?.total ?? prev.total ?? 0,
              current: currentPagination.current,
              pageSize: currentPagination.pageSize
            }));
          }
        } else if (res.status === 404) {
          if (currentTab === 'bidders') {
            setRefundRequestList([]);
          } else {
            setBailiffRefundRequestList([]);
          }
          message.info('No New refund request found');
        } else {
          // fallback for other statuses
          message.info(res?.message || 'No data available');
        }
      })
      .catch((err: any) => {
        setLoading(false);
        if (err?.message !== 'Operation canceled due to new request.') {
          message.error(err?.message || 'Failed to fetch refund requests');
        }
      });
  };

  // Called by Antd Table when pagination / sorter / filters change
  const handleTableChange = (newPagination: any, _filters?: any, _sorter?: any) => {
    const pageConfig = {
      current: newPagination?.current ?? 1,
      pageSize: newPagination?.pageSize ?? pagination.pageSize
    };
    setPagination((prev) => ({ ...prev, ...pageConfig }));
    // fetch with updated pagination right away
    OnRefundRequestSearch(undefined, activeTab, pageConfig);
  };

  // Columns (unchanged logic); used existing column arrays with minor safety checks
  const bailiffsColumns: ColumnsType<DataType> = [
    {
      title: 'No.',
      dataIndex: 'index',
      key: 'index',
      width: 50,
      render: (_text, _record, idx) => {
        const pageNumber = pagination.current || 1;
        const pageSize = pagination.pageSize || 10;
        const globalIndex = (pageNumber - 1) * pageSize + (idx + 1);
        return <span>{globalIndex}</span>;
      },
      sorter: (a: any, b: any) => (a.index ?? 0) - (b.index ?? 0),
      sortOrder: sortedInfo.columnKey === 'index' ? sortedInfo.order : null,
      ellipsis: false
    },
    {
      title: 'Transaction Reference',
      dataIndex: 'transaction_reference',
      key: 'transaction_reference',
      width: 100,
      sorter: (a: any, b: any) =>
        (a?.transaction_reference ?? '').localeCompare(b?.transaction_reference ?? ''),
      sortOrder:
        sortedInfo?.columnKey === 'transaction_reference' ? sortedInfo?.order : null,
      ellipsis: true
    },
    {
      title: 'Beneficiary Account Name',
      dataIndex: 'beneficiary_account_name',
      key: 'beneficiary_account_name',
      width: 250,
      sorter: (a: any, b: any) =>
        (a?.beneficiary_account_name ?? '').localeCompare(b?.beneficiary_account_name ?? ''),
      sortOrder:
        sortedInfo?.columnKey === 'beneficiary_account_name' ? sortedInfo?.order : null,
      render: (text: string) => (text ? text.toUpperCase() : '')
    },
    {
      title: 'Beneficiary Bank Name',
      dataIndex: 'beneficiary_bank_name',
      key: 'beneficiary_bank_name',
      sorter: (a: any, b: any) =>
        (a?.beneficiary_bank_name ?? '').localeCompare(b?.beneficiary_bank_name ?? ''),
      sortOrder:
        sortedInfo?.columnKey === 'beneficiary_bank_name' ? sortedInfo?.order : null,
      ellipsis: true
    },
    {
      title: 'Beneficiary Account Number',
      dataIndex: 'beneficiary_account_number',
      key: 'beneficiary_account_number',
      sorter: (a: any, b: any) =>
        (a?.beneficiary_account_number ?? '').localeCompare(b?.beneficiary_account_number ?? ''),
      sortOrder:
        sortedInfo?.columnKey === 'beneficiary_account_number' ? sortedInfo?.order : null,
      ellipsis: true
    },
    {
      title: 'Amount',
      dataIndex: 'amount',
      key: 'amount',
      render: (_text: any, record: any) => {
        const amount = parseInt(String(record?.amount ?? '0'), 10) || 0;
        return (
          <span style={{ color: '#37762f', fontWeight: 800 }}>
            {amount.toLocaleString()} <span style={{ fontWeight: 600, color: '#6b7280' }}>Rwf</span>
          </span>
        );
      },
      sorter: (a: any, b: any) => Number(a?.amount ?? 0) - Number(b?.amount ?? 0),
      sortOrder: sortedInfo?.columnKey === 'amount' ? sortedInfo?.order : null,
      ellipsis: true,
      align: 'right'
    },
    {
      title: 'Status',
      dataIndex: 'status',
      render: (text: any, record: any) => {
        const status = String(record?.status ?? text ?? '').trim();
        let displayStatus = status;
        let color = 'default';

        if (status === 'PENDING') {
          displayStatus = 'Pending Refund';
          color = 'warning';
        } else if (status === 'INITIATED') {
          displayStatus = 'Initiated Refund';
          color = 'processing';
        } else if (status === 'COMPLETED' || status === 'APPROVED' || status === 'Downloaded to be Refunded') {
          displayStatus = 'Completed Refund';
          color = 'success';
        } else if (status === 'RECEIVED REQUEST') {
          displayStatus = 'Received Request';
          color = 'processing';
        } else if (status === 'REJECTED') {
          displayStatus = 'Rejected';
          color = 'error';
        }

        return (
          <Tag color={color} style={{ borderRadius: 999, padding: '2px 10px', fontWeight: 600 }}>
            {displayStatus || '-'}
          </Tag>
        );
      },
      key: 'status',
      width: 180,
      sorter: (a: any, b: any) => (a?.status ?? '').localeCompare(b?.status ?? ''),
      sortOrder: sortedInfo?.columnKey === 'status' ? sortedInfo?.order : null,
      ellipsis: true
    },
    {
      title: 'Action',
      dataIndex: 'action',
      width: 100,
      render: (_text: any, record: any) => (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%' }}>
          <i
            className="far fa-eye cursor-pointer icon-style"
            onClick={() => {
              navigate('/view-refund-request', { state: record });
            }}
          />
        </div>
      )
    }
  ];

  const biddersColumns: ColumnsType<DataType> = [
    {
      title: 'No.',
      dataIndex: 'index',
      key: 'index',
      width: 50,
      render: (_text, _record, idx) => {
        const pageNumber = pagination.current || 1;
        const pageSize = pagination.pageSize || 10;
        const globalIndex = (pageNumber - 1) * pageSize + (idx + 1);
        return <span>{globalIndex}</span>;
      },
      sorter: (a: any, b: any) => (a.index ?? 0) - (b.index ?? 0),
      sortOrder: sortedInfo.columnKey === 'index' ? sortedInfo.order : null,
      ellipsis: false
    },
    {
      title: 'Transaction Reference',
      dataIndex: 'transaction_reference',
      key: 'transaction_reference',
      width: 100,
      sorter: (a: any, b: any) =>
        (a?.transaction_reference ?? '').localeCompare(b?.transaction_reference ?? ''),
      sortOrder:
        sortedInfo?.columnKey === 'transaction_reference' ? sortedInfo?.order : null,
      ellipsis: true
    },
    {
      title: 'Beneficiary Account Name',
      dataIndex: 'beneficiary_account_name',
      key: 'beneficiary_account_name',
      width: 250,
      sorter: (a: any, b: any) =>
        (a?.beneficiary_account_name ?? '').localeCompare(b?.beneficiary_account_name ?? ''),
      sortOrder:
        sortedInfo?.columnKey === 'beneficiary_account_name' ? sortedInfo?.order : null,
      render: (text: string) => (text ? text.toUpperCase() : '')
    },
    {
      title: 'Beneficiary Bank Name',
      dataIndex: 'beneficiary_bank_name',
      key: 'beneficiary_bank_name',
      sorter: (a: any, b: any) =>
        (a?.beneficiary_bank_name ?? '').localeCompare(b?.beneficiary_bank_name ?? ''),
      sortOrder:
        sortedInfo?.columnKey === 'beneficiary_bank_name' ? sortedInfo?.order : null,
      ellipsis: true
    },
    {
      title: 'Beneficiary Account Number',
      dataIndex: 'beneficiary_account_number',
      key: 'beneficiary_account_number',
      sorter: (a: any, b: any) =>
        (a?.beneficiary_account_number ?? '').localeCompare(b?.beneficiary_account_number ?? ''),
      sortOrder:
        sortedInfo?.columnKey === 'beneficiary_account_number' ? sortedInfo?.order : null,
      ellipsis: true
    },
    {
      title: 'Amount',
      dataIndex: 'amount',
      key: 'amount',
      render: (_text: any, record: any) => {
        const amount = parseInt(String(record?.amount ?? '0'), 10) || 0;
        return (
          <span style={{ color: '#37762f', fontWeight: 800 }}>
            {amount.toLocaleString()} <span style={{ fontWeight: 600, color: '#6b7280' }}>Rwf</span>
          </span>
        );
      },
      sorter: (a: any, b: any) => Number(a?.amount ?? 0) - Number(b?.amount ?? 0),
      sortOrder: sortedInfo?.columnKey === 'amount' ? sortedInfo?.order : null,
      ellipsis: true,
      align: 'right'
    },
    {
      title: 'Status',
      dataIndex: 'status',
      render: (text: any, record: any) => {
        const status = String(record?.status ?? text ?? '').trim();
        let displayStatus = status;
        let color = 'default';

        if (status === 'PENDING') {
          displayStatus = 'Pending Refund';
          color = 'warning';
        } else if (status === 'INITIATED') {
          displayStatus = 'Initiated Refund';
          color = 'processing';
        } else if (status === 'COMPLETED' || status === 'APPROVED' || status === 'Downloaded to be Refunded') {
          displayStatus = 'Completed Refund';
          color = 'success';
        } else if (status === 'RECEIVED REQUEST') {
          displayStatus = 'Received Request';
          color = 'processing';
        } else if (status === 'REJECTED') {
          displayStatus = 'Rejected';
          color = 'error';
        }

        return (
          <Tag color={color} style={{ borderRadius: 999, padding: '2px 10px', fontWeight: 600 }}>
            {displayStatus || '-'}
          </Tag>
        );
      },
      key: 'status',
      width: 180,
      sorter: (a: any, b: any) => (a?.status ?? '').localeCompare(b?.status ?? ''),
      sortOrder: sortedInfo?.columnKey === 'status' ? sortedInfo?.order : null,
      ellipsis: true
    },
    {
      title: 'Action',
      dataIndex: 'action',
      width: 100,
      render: (_text: any, record: any) => (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%' }}>
          <i
            className="far fa-eye cursor-pointer icon-style"
            onClick={() => {
              navigate('/view-refund-request', { state: record });
            }}
          />
        </div>
      )
    }
  ];

  // Determine a safe row key
  const rowKeyFn = (record: any) =>
    record.transaction_reference ?? record.key ?? `${record.index ?? 'i'}-${record.beneficiary_account_number ?? Math.random()}`;

  return (
    <div style={{ width: '100%' }}>
      {/* Enhanced Custom CSS Styles - Matching UsersAccountsTable */}
      <style>{`
        .enhanced-card {
          background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
          border: 1px solid #e5e7eb;
          border-radius: 12px;
          box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
          transition: all 0.2s ease;
        }
        .enhanced-card:hover {
          box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
          transform: translateY(-2px);
        }
        .enhanced-table-row:nth-child(even) {
          background-color: #f8fafc;
        }
        .enhanced-table-row:hover {
          background-color: #e2e8f0;
        }
        .enhanced-table-header {
          background: linear-gradient(135deg, #37762f 0%, #4a7c59 100%);
          color: white;
          font-weight: 600;
        }
        .ant-table-thead > tr > th {
          background: none !important;
          border-bottom: 2px solid #cbd5e1 !important;
          font-weight: 600 !important;
          color: #374151 !important;
        }
        .ant-table-tbody > tr > td {
          border-bottom: 1px solid #e5e7eb !important;
        }
        .refund-table .ant-table-container {
          border-radius: 14px;
          overflow: hidden;
        }
        .refund-table .ant-table-thead > tr > th {
          position: sticky;
          top: 0;
          z-index: 2;
          background: linear-gradient(135deg, rgba(55, 118, 47, 0.10) 0%, rgba(74, 124, 89, 0.06) 100%) !important;
          backdrop-filter: blur(6px);
        }
        .refund-table .ant-table-tbody > tr:hover > td {
          background: rgba(102, 126, 234, 0.06) !important;
        }
        .refund-table .ant-table-pagination {
          margin: 12px 0 0 0 !important;
        }
      `}</style>

      {/* Enhanced Page Header - Matching BiddersRefundRequest */}
      <div className="px-5 py-4 border-b-slate-200 border border-solid" style={{
        background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
        borderRadius: '0 0 12px 12px',
        marginBottom: '20px',
      }}>
        <div className="flex justify-between items-center">
          <div className="text-title font-bold flex items-center">
            <div style={{
              background: 'linear-gradient(135deg, #37762f 0%, #4a7c59 100%)',
              borderRadius: '50%',
              width: '48px',
              height: '48px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginRight: '16px',
              boxShadow: '0 4px 12px rgba(55, 118, 47, 0.3)',
            }}>
              <span style={{ fontSize: '24px' }}>💸</span>
            </div>
            <div>
              <h2 className="text-title font-bold" style={{
                margin: 0,
                background: 'linear-gradient(135deg, #37762f 0%, #4a7c59 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
              }}>
                Refund Requests
              </h2>
              <p style={{
                margin: '4px 0 0 0',
                fontSize: '14px',
                color: '#6b7280',
                fontWeight: '500',
              }}>
                Financial Refund Management • Process & Track Requests
              </p>
            </div>
          </div>
          <div className="md:w-1/5"></div>
        </div>
      </div>

      <div style={{ margin: '0 20px' }}>
        <RefundStatsRangeAlert fromDate={fromDate} toDate={toDate} />
      </div>
      {/* Enhanced stats section with hover effects - Matching BiddersRefundRequest */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'stretch',
        gap: '20px',
        padding: '20px',
        backgroundColor: '#fafafa',
        borderRadius: '8px',
        margin: '0 20px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
      }}>
        <div
          style={{
            flex: '1',
            minWidth: 180,
            background: '#F5F5F5',
            padding: '20px',
            borderRadius: '12px',
            border: '1px solid #e5e7eb',
            textAlign: 'center',
            color: '#1e293b',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
            transition: 'transform 0.2s ease, box-shadow 0.2s ease',
            cursor: 'default',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-2px)';
            e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.1)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.05)';
          }}
        >
          <div style={{ fontSize: '14px', color: '#64748b', marginBottom: '8px', fontWeight: '500' }}>
            Total Amount Requested
          </div>
          <div style={{ fontSize: '18px', fontWeight: 'bold', color: 'green' }}>
            {((parseMoneyAmount(BailiffAmountRequestedToBeRefunded) ?? 0) + (parseMoneyAmount(BiddersAmountRequestedToBeRefund) ?? 0)).toLocaleString()} Rwf
          </div>
        </div>

        <div
          style={{
            flex: '1',
            minWidth: 180,
            background: '#F5F5F5',
            padding: '20px',
            borderRadius: '12px',
            border: '1px solid #e5e7eb',
            textAlign: 'center',
            color: '#1e293b',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
            transition: 'transform 0.2s ease, box-shadow 0.2s ease',
            cursor: 'default',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-2px)';
            e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.1)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.05)';
          }}
        >
          <div style={{ fontSize: '14px', color: '#64748b', marginBottom: '8px', fontWeight: '500' }}>
            New Amount Requested
          </div>
          <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#37762f' }}>
            {((parseMoneyAmount(NewBiddersAmountRequestedToBeRefunded) ?? 0) + (parseMoneyAmount(NewBailiffAmountRequestedToBeRefunded) ?? 0)).toLocaleString()} Rwf
          </div>
        </div>

        <div
          style={{
            flex: '1',
            minWidth: 180,
            background: '#F5F5F5',
            padding: '20px',
            borderRadius: '12px',
            border: '1px solid #e5e7eb',
            textAlign: 'center',
            color: '#1e293b',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
            transition: 'transform 0.2s ease, box-shadow 0.2s ease',
            cursor: 'default',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-2px)';
            e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.1)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.05)';
          }}
        >
          <div style={{ fontSize: '14px', color: '#64748b', marginBottom: '8px', fontWeight: '500' }}>
            Remaining to Request
          </div>
          <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#37762f' }}>
            {Math.max(
              0,
              parseMoneyAmount(totalPaidForRange) - (parseMoneyAmount(BiddersAmountRequestedToBeRefund) + parseMoneyAmount(BailiffAmountRequestedToBeRefunded))
            ).toLocaleString()} Rwf
          </div>
        </div>

        <div
          style={{
            flex: '1',
            minWidth: 180,
            background: '#F5F5F5',
            padding: '20px',
            borderRadius: '12px',
            border: '1px solid #e5e7eb',
            textAlign: 'center',
            color: '#1e293b',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
            transition: 'transform 0.2s ease, box-shadow 0.2s ease',
            cursor: 'default',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-2px)';
            e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.1)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.05)';
          }}
        >
          <div style={{ fontSize: '14px', color: '#64748b', marginBottom: '8px', fontWeight: '500' }}>
            Total Paid Amount
          </div>
          <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#37762f' }}>
            {(parseMoneyAmount(totalPaidForRange)).toLocaleString()} Rwf
          </div>
        </div>
      </div>

      <div style={{ width: '100%' }}>
        <div className="py-0">
          <Form
            name="normal_login"
            layout="vertical"
            className="login-form p-10"
            initialValues={{ remember: true }}
            form={form}
          >
            <Card className="bg-slate-100 responsive-card" style={{ width: '100%' }}>
              <Row gutter={[16, 16]}>
                <Col xs={24} xl={18}>
                  <Form.Item name="range_picker">
                    <RangePicker
                      size="large"
                      className="w-full"
                      allowClear
                      onChange={(value, valueString) => {
                        setFromDate(valueString[0]);
                        setToDate(valueString[1]);
                        // reset to page 1 and fetch
                        const resetPagination = { current: 1, pageSize: pagination.pageSize };
                        setPagination((prev) => ({ ...prev, current: 1 }));
                        OnRefundRequestSearch(undefined, activeTab, resetPagination);
                      }}
                    />
                  </Form.Item>
                  {filter !== '' ? (
                    <div className="flex flex-col md:flex-row justify-between pb-2.5 md:pb-0 gap-2">
                      <div>
                        Applied filters: <span className="font-medium">{filter}</span>
                      </div>
                      <div
                        className="bg-black text-center py-0.5 px-2.5 text-white rounded-sm font-medium cursor-pointer"
                        onClick={clearFilter}
                      >
                        Clear X
                      </div>
                    </div>
                  ) : (
                    ''
                  )}
                </Col>
                <Col xs={24} xl={6}>
                  <Form.Item>
                    <Button
                      type="primary"
                      size="large"
                      className="w-full font-bold"
                      style={{
                        background: '#37762f',
                        borderColor: '#37762f'
                      }}
                      disabled={fromDate.length === 0 || toDate.length === 0}
                      onClick={() => {
                        // apply date filter explicitly
                        const resetPagination = { current: 1, pageSize: pagination.pageSize };
                        setPagination((prev) => ({ ...prev, current: 1 }));
                        OnRefundRequestSearch(undefined, activeTab, resetPagination);
                      }}
                    >
                      <div className="float-left">
                        <i className="fa-file-excel" />
                      </div>
                      FILTER
                    </Button>
                  </Form.Item>
                </Col>
              </Row>
            </Card>
          </Form>
        </div>

        <Card
          style={{
            marginTop: '8px',
            borderRadius: '16px',
            boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
            border: '1px solid #e5e7eb',
            overflow: 'hidden'
          }}
        >
          <div style={{
            background: '#dff0de',
            color: '#000000',
            fontWeight: 'normal',
            padding: '16px 20px',
            margin: '-24px -24px 24px -24px',
              display: 'flex',
              alignItems: 'center',
            justifyContent: 'space-between'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <i className="fas fa-users" style={{ fontSize: '24px' }}></i>
              <span style={{ fontSize: '18px', fontWeight: 'normal' }}>
                {activeTab === 'bidders' 
                  ? `All Bidders (${refundRequestList.length})`
                  : `All Bailiffs (${bailiffRefundRequestList.length})`
                }
              </span>
            </div>
          </div>

          <Tabs
            onChange={(key) => handleTabChange(key)}
            type="card"
            size="large"
            defaultActiveKey="bidders"
            activeKey={activeTab}
            tabBarExtraContent={
              <div className="w-full flex flex-col md:flex-row md:items-center md:space-x-4 space-y-2 md:space-y-0">
                <Button className="md:ml-2 w-full md:w-auto" style={{ marginRight: 0 }}>
                  Download Report
                </Button>
                <Button type="link" className="md:ml-2 w-full md:w-auto" onClick={clearFilter}>
                  Clear Filter
                </Button>
              </div>
            }
            items={[
              {
                key: 'bidders',
                label: 'Bidders',
                children: (
                  <div>
                    <div style={{ marginBottom: '20px' }}>
                      <Input
                        size="large"
                        placeholder="Search by transaction reference, beneficiary name, or account number..."
                        prefix={<ZoomOutOutlined style={{ color: '#667eea', fontSize: '18px' }} />}
                        allowClear
                        onChange={OnRefundRequestSearch}
                        style={{
                          borderRadius: '10px',
                          border: '2px solid #e5e7eb',
                          boxShadow: '0 6px 18px rgba(17, 24, 39, 0.06)',
                          background: 'linear-gradient(180deg, #ffffff 0%, #fbfdff 100%)'
                        }}
                      />
                    </div>

                    <div style={{ overflowX: 'auto' }}>
                      {loading
                        ? (
                          <div style={{ textAlign: 'center', padding: '60px 20px' }}>
                            <div style={{ fontSize: '48px', marginBottom: '16px' }}>⏳</div>
                            <div style={{ color: '#9ca3af', fontSize: '16px' }}>Loading refund requests...</div>
                          </div>
                          )
                        : (
                      <DynamicTable
                        data={refundRequestList}
                        useCustomColumns={true}
                        customColumnsConfig={biddersColumns}
                        onChange={handleTableChange}
                        className="refund-table"
                        bordered
                        size="middle"
                        sticky
                        pagination={{
                          current: pagination.current,
                          pageSize: pagination.pageSize,
                          total: pagination.total
                        }}
                        loading={loading}
                            rowClassName={(record, index) => index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}
                      />
                          )}
                    </div>
                  </div>
                )
              },
              {
                key: 'bailiffs',
                label: 'Bailiffs',
                children: (
                  <div>
                    <div style={{ marginBottom: '20px' }}>
                      <Input
                        size="large"
                        placeholder="Search by transaction reference, beneficiary name, or account number..."
                        prefix={<ZoomOutOutlined style={{ color: '#667eea', fontSize: '18px' }} />}
                        allowClear
                        onChange={OnRefundRequestSearch}
                        style={{
                          borderRadius: '10px',
                          border: '2px solid #e5e7eb',
                          boxShadow: '0 6px 18px rgba(17, 24, 39, 0.06)',
                          background: 'linear-gradient(180deg, #ffffff 0%, #fbfdff 100%)'
                        }}
                      />
                    </div>

                    <div style={{ overflowX: 'auto' }}>
                      {loading
                        ? (
                          <div style={{ textAlign: 'center', padding: '60px 20px' }}>
                            <div style={{ fontSize: '48px', marginBottom: '16px' }}>⏳</div>
                            <div style={{ color: '#9ca3af', fontSize: '16px' }}>Loading refund requests...</div>
                          </div>
                          )
                        : (
                      <DynamicTable
                        data={bailiffRefundRequestList}
                        useCustomColumns={true}
                        customColumnsConfig={bailiffsColumns}
                        onChange={handleTableChange}
                        className="refund-table"
                        bordered
                        size="middle"
                        sticky
                        pagination={{
                          current: pagination.current,
                          pageSize: pagination.pageSize,
                          total: pagination.total
                        }}
                        loading={loading}
                            rowClassName={(record, index) => index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}
                      />
                          )}
                    </div>
                  </div>
                )
              }
            ]}
          />
        </Card>
      </div>
    </div>
  );
};

export default RefundRequestList;
